import Cookie from '@wetransfer/concorde-cookie';
import Browser from '@wetransfer/concorde-browser';

import React from 'react';

import { AppConfig } from '../core-bundle/config/app-config';

import Helper from '@app/helper';
import Store from '@app/store';

import TermsAndConditions from '@module/terms-and-conditions';
import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import { PLUS_ACCOUNT, FREE } from '@constant/subscriptions';
import { EXPERIMENT_FREE_ACCOUNT } from '@constant/experiments';

import LaunchDarkly from '@module/launchdarkly';

// Write cookie with date and amount of transfers sent.
function writeCookie() {
  const now = new Date();
  const formatted = `${
    now.getMonth() + 1
  }/${now.getDate()}/${now.getFullYear()}`;
  const sent = Helper.getWTSent();
  const cookieValue = [formatted, sent].join(',');
  Cookie.set(AppConfig.cookies.takeover.name, cookieValue, { days: 365 });
}

function readCookie() {
  let cookie = Cookie.get(AppConfig.cookies.takeover.name);
  if (!cookie) {
    return false;
  }

  // Examine cookie
  cookie = cookie.split(',');
  const [date, filesSent] = cookie;
  if (!date || !filesSent) {
    return false;
  }
  const sent = parseInt(filesSent || 0, 10);

  return { date, sent };
}

function hasValidCookie() {
  const previously = readCookie();
  if (!previously) {
    return false;
  }

  // When was this set?
  const daysSince = Helper.getDaysBetweenDates(previously.date, new Date());

  // Check if daysSince is a number
  if (isNaN(daysSince)) {
    return false;
  }

  // How many transfers have been sent?
  const sent = Helper.getWTSent();
  // How many transfers have been done since?
  const sentSince = sent - previously.sent;

  // Don't show if less than 7 days have expired and less than 20 transfers have been done
  if (daysSince < 7 && sentSince < 20) {
    return true;
  }

  return false;
}

// This diagram is out of date. But we can keep it for future reference when we refactor this page.
// https://code2flow.com/D0hxyC : contains all the logic to show/not show the Splashpage (out of date)
function shouldDisplaySplashpage() {
  const isVisitingFirstTime = Helper.getFirstVisitDate() === undefined;
  if (isVisitingFirstTime) {
    return false;
  }

  if (Browser.isMobile || window.innerWidth <= 950) {
    return false;
  }

  const { route, user, channel } = Store.getState();

  // Only on the upload page (this catches both download/:id and /wallpaper/:id) and panel open
  if (route.type !== 'transfer' || route.params.id) {
    return false;
  }

  // Not in IE9 or lower
  if (Browser.matches('explorer <= 9.0')) {
    return false;
  }

  // Not for logged in users.
  const plusCookie = parseInt(Cookie.get('wt_plus'), 10) === 1;
  if (user.loggedIn || plusCookie) {
    return false;
  }

  // Not when T&C are not accepted
  if (!TermsAndConditions.areAccepted()) {
    return false;
  }

  // Don't show on channels
  if (channel && channel.name) {
    return false;
  }

  // Verify existing cookie (if any)
  if (hasValidCookie()) {
    return false;
  }

  return true;
}

const trackShow = () => {
  Analytics.trackSnowplowEvent({
    category: 'splashscreen',
    action: 'show_page',
  });
  Analytics.trackWithPixelOrSDK('WeTransfer_Site_Landing_Page_Overlay_Visit');
};

const trackFreeFunnel = (track = { action: 'click_take_me_to_free' }) => {
  Analytics.trackSnowplowEvent({
    category: 'splashscreen',
    action: track.action,
  });
  Analytics.trackWithPixelOrSDK(
    'WeTransfer_Site_Landing_Page_Overlay_WT_Free_Button_Conversion'
  );
};

const trackFreeAccountFunnel = () => {
  Analytics.trackSnowplowEvent({
    category: 'splashscreen',
    action: 'click_sign_up_for_free',
  });
};

const trackPlusFunnel = (track = { action: 'click_get_wetransfer_plus' }) => {
  Analytics.trackSnowplowEvent({
    category: 'splashscreen',
    action: track.action,
  });
};

const makeFreeAccountFeatureList = () => {
  if (LaunchDarkly.getVariant(EXPERIMENT_FREE_ACCOUNT)) {
    return (
      <React.Fragment>
        <li className="plan__feature--unavailable">
          {I18n.t('splash.plan.free_account.feature_1')}
        </li>
        <li>
          {I18n.t('splash.plan.free_account.feature_2', {
            free_transfer_limit: FREE.TRANSFER_LIMIT,
          })}
        </li>
        <li>{I18n.t('splash.plan.free_account.feature_3')}</li>
      </React.Fragment>
    );
  }

  // Clean this up once we roll out free accounts globally (Nov 2019)
  return (
    <React.Fragment>
      <li>
        {I18n.t('splash.plan.free.feature_1', {
          free_transfer_limit: FREE.TRANSFER_LIMIT,
        })}
      </li>
      <li className="plan__features--is-faded">
        {I18n.t('splash.plan.plus.feature_2')}
      </li>
      <li className="plan__features--is-faded">
        {I18n.t('splash.plan.plus.feature_3')}
      </li>
      <li className="plan__features--is-faded">
        {I18n.t('splash.plan.plus.feature_4')}
      </li>
    </React.Fragment>
  );
};

const makeProFeatureList = () => {
  if (LaunchDarkly.getVariant(EXPERIMENT_FREE_ACCOUNT)) {
    return (
      <React.Fragment>
        <li>
          {I18n.t('splash.plan.pro.feature_1', {
            storage_limit_pro: PLUS_ACCOUNT.STORAGE_LIMIT,
          })}
        </li>
        <li>
          {I18n.t('splash.plan.pro.feature_2', {
            transfer_limit_pro: PLUS_ACCOUNT.TRANSFER_LIMIT,
          })}
        </li>
        <li>{I18n.t('splash.plan.pro.feature_3')}</li>
        <li>{I18n.t('splash.plan.pro.feature_4')}</li>
        <li>{I18n.t('splash.plan.pro.feature_5')}</li>
      </React.Fragment>
    );
  }

  // Clean this up once we roll out free accounts globally (Nov 2019)
  return (
    <React.Fragment>
      <li>
        {I18n.t('splash.plan.plus.feature_1', {
          plus_transfer_limit: PLUS_ACCOUNT.TRANSFER_LIMIT,
        })}
      </li>
      <li>
        {I18n.t('splash.plan.plus.feature_11', {
          plus_storage_limit: PLUS_ACCOUNT.STORAGE_LIMIT,
        })}
      </li>
      <li>{I18n.t('splash.plan.plus.feature_12')}</li>
      <li>{I18n.t('splash.plan.plus.feature_13')}</li>
    </React.Fragment>
  );
};

const content = (key) => {
  if (typeof key !== 'string') {
    return '';
  }
  if (key.length === 0) {
    return '';
  }
  return I18n.t(key);
};

export default {
  shouldDisplaySplashpage,
  writeCookie,
  trackShow,
  trackFreeFunnel,
  trackFreeAccountFunnel,
  trackPlusFunnel,
  makeFreeAccountFeatureList,
  makeProFeatureList,
  content,
};
